import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useI18n } from '../../../../i18n/I18nProvider';

const Bar = styled.nav`
  position: fixed !important;
  z-index: 1200;
  left: max(.45rem, env(safe-area-inset-left));
  right: max(.45rem, env(safe-area-inset-right));
  bottom: max(.45rem, env(safe-area-inset-bottom));
  display: none;
  align-items: stretch;
  gap: .12rem;
  width: auto;
  max-width: 36rem;
  margin: 0 auto;
  padding: .38rem;
  border: 1px solid rgba(255,255,255,.14);
  border-radius: 1.35rem;
  background: rgba(5,5,5,.97);
  box-shadow: 0 1rem 2.8rem rgba(0,0,0,.32), inset 0 1px 0 rgba(255,255,255,.06);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  isolation: isolate;

  @media (max-width: 56rem) {
    display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  }
`;

const Item = styled(NavLink)`
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  min-height: 3.35rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .16rem;
  padding: .34rem .14rem;
  border-radius: 1rem;
  color: rgba(255,255,255,.62);
  font-size: .61rem;
  font-weight: 800;
  line-height: 1.05;
  text-align: center;
  text-decoration: none;
  transition: background 160ms ease, color 160ms ease, transform 160ms ease;

  svg {
    flex: 0 0 auto;
    font-size: 1.2rem;
  }

  span {
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.active {
    color: #080808;
    background: #c0a375;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,.2), 0 .35rem .9rem rgba(192,163,117,.18);
  }

  &:active {
    transform: scale(.96);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  @media (max-width: 22rem) {
    min-height: 3.15rem;
    padding-inline: .08rem;
    font-size: .55rem;

    svg {
      font-size: 1.12rem;
    }
  }
`;

const LabelBottomNavigation = ({ userType }) => {
  const { t } = useI18n();

  const clientItems = [
    { to: '/demands', label: t('nav.mobileDemands'), icon: <AssignmentOutlinedIcon /> },
    { to: '/projects', label: t('nav.history'), icon: <HistoryOutlinedIcon /> },
    { to: '/profile', label: t('nav.mobileProfile'), icon: <PersonOutlineIcon /> },
  ];

  const artisanItems = [
    { to: '/jobs', label: t('nav.mobileMissions'), icon: <AssignmentOutlinedIcon /> },
    { to: '/planning', label: t('nav.mobilePlanning'), icon: <CalendarMonthOutlinedIcon /> },
    { to: '/newjobs', label: t('nav.explore'), icon: <ExploreOutlinedIcon /> },
    { to: '/boutique', label: t('nav.showcase'), icon: <StorefrontOutlinedIcon /> },
    { to: '/profileartisan', label: t('nav.mobileProfile'), icon: <PersonOutlineIcon /> },
  ];

  const [immersiveOpen, setImmersiveOpen] = useState(false);
  const immersiveSources = useRef(new Set());
  const items = userType === 'Client' ? clientItems : userType === 'Artisan' ? artisanItems : [];

  useEffect(() => {
    const setFromEvent = (event) => {
      const source = String(event?.detail?.source || event?.type || 'immersive');
      if (event?.detail?.open) immersiveSources.current.add(source);
      else immersiveSources.current.delete(source);
      setImmersiveOpen(immersiveSources.current.size > 0);
    };

    window.addEventListener('lpb:immersive-state', setFromEvent);
    window.addEventListener('lpb:stripe-embedded-state', setFromEvent);

    return () => {
      window.removeEventListener('lpb:immersive-state', setFromEvent);
      window.removeEventListener('lpb:stripe-embedded-state', setFromEvent);
      immersiveSources.current.clear();
    };
  }, []);

  if (!items.length || typeof document === 'undefined') return null;

  return createPortal(
    <Bar aria-label="Navigation mobile" $hidden={immersiveOpen}>
      {items.map((item) => (
        <Item
          key={item.to}
          exact
          to={item.to}
          activeClassName="active"
          aria-label={item.label}
        >
          {item.icon}
          <span>{item.label}</span>
        </Item>
      ))}
    </Bar>,
    document.body,
  );
};

const mapStateToProps = ({ firebase }) => ({
  userType: firebase.profile && firebase.profile.user_type,
});

export default connect(mapStateToProps)(LabelBottomNavigation);
